<template>
    <div>
        <div class="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
            <!-- logo -->
            <AppLogoForAuth />
            <!-- header -->
            <h2 class="text-3xl font-bold text-cost-blue mb-8 text-center">Login</h2>
            <!-- error messages -->
            <p v-if="errorMessage" class="error-message">
                <AppIconExclamationTriangle class="error-icon" />
                {{ errorMessage }}
            </p>
            <!-- login form -->
            <form @submit.prevent="handleLogin" class="auth-form">
                <!-- email -->
                <div class="input-group">
                    <label for="email" class="input-label">Email Address</label>
                    <div class="input-wrapper">
                        <AppIconEnvelope class="input-icon" />
                        <input type="email" id="email" v-model="email" placeholder="member@example.com"
                            class="auth-input" required />
                    </div>
                </div>
                <!-- password -->
                <div class="input-group">
                    <label for="password" class="input-label">Password</label>
                    <div class="input-wrapper">
                        <AppIconLockClosed class="input-icon" />
                        <input type="password" id="password" v-model="password" placeholder="••••••••"
                            class="auth-input" required />
                    </div>
                </div>
                <!-- cloudflare turnsite -->
                <!-- {{ token }} -->
                <NuxtTurnstile v-model="token" class="text-center" />
                <!-- sign in button -->
                <button type="submit" class="auth-button relative" :disabled="isLoading" @click="handleButtonClick">
                    <span class="flex items-center justify-center">
                        <svg v-if="isLoading" class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                            </circle>
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                            </path>
                        </svg>
                        <span>{{ isLoading ? 'Signing In...' : 'Sign In' }}</span>
                    </span>
                    <span class="absolute inset-0 bg-red-600 rounded-lg opacity-0 transition-opacity duration-300"
                        :class="{ 'active-click-effect': isClicking }"></span>
                </button>
                <!-- quick links -->
                <div class="auth-footer">
                    <NuxtLink to="/auth/signup" class="auth-link">
                        Create Account
                        <AppIconArrowRight class="link-arrow" />
                    </NuxtLink>
                    <NuxtLink to="/auth/forgot-password" class="auth-link">
                        Forgot Password?
                    </NuxtLink>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
definePageMeta({
    layout: 'auth',
    public: true,
    middleware: ['redirect-if-authenticated']
})

import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { signIn } from '~/composables/useAuth'

const token = ref('')
const email = ref('')
const password = ref('')
const errorMessage = ref('')
const isLoading = ref(false)
const isClicking = ref(false)
const router = useRouter()

const handleLogin = async () => {
    // Reset error message and set loading state to true
    errorMessage.value = ''
    isLoading.value = true
    try {
        // Attempt to sign in using the signIn function from useAuth composable
        const user = await signIn(email.value, password.value, token.value)
        // Redirect to the dashboard on successful login
        router.push('/dashboard')
    } catch (error) {
        // Display error message if login fails
        errorMessage.value = error.message || 'Login failed. Please try again.'
    } finally {
        isLoading.value = false
    }
}
const handleButtonClick = () => {
    isClicking.value = true
    setTimeout(() => isClicking.value = false, 150)
}

</script>

<style scoped>
.auth-container {
    @apply max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg;
}

.brand-title {
    @apply text-4xl font-bold text-center mb-4;
}

.auth-title {
    @apply text-2xl font-semibold text-center mb-8;
}

.auth-form {
    @apply space-y-6;
}

.input-group {
    @apply space-y-2;
}

.input-label {
    @apply block text-sm font-medium text-gray-700;
}

.input-wrapper {
    @apply relative;
}

.input-icon {
    @apply w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400;
}

.auth-input {
    @apply w-full px-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cost-red focus:border-cost-red;
}

.auth-button {
    /* @apply w-full bg-cost-red text-white py-3 rounded-lg hover:bg-red-600 transition-colors font-semibold; */
    @apply w-full bg-cost-red text-white py-3 rounded-lg hover:bg-red-600 transition-all duration-300 active:scale-95 font-semibold relative overflow-hidden;
}

.active-click-effect {
    animation: ripple 0.6s ease-out;
}

@keyframes ripple {
    from {
        opacity: 0.4;
        transform: scale(0);
    }

    to {
        opacity: 0;
        transform: scale(2);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.error-message {
    @apply flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-lg;
}

.error-icon {
    @apply w-5 h-5 mr-2;
}

.auth-footer {
    @apply pt-4 border-t border-gray-200 space-y-3;
}

.auth-link {
    @apply flex items-center justify-center text-cost-red hover:text-red-600 transition-colors text-sm font-medium;
}

.link-arrow {
    @apply w-4 h-4 ml-1;
}

.text-cost-red {
    color: #e31936;
}

.text-cost-blue {
    color: #005dab;
}
</style>